import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollingDone, setPollingDone] = useState(false);
  const pollCountRef = useRef(0);

  const sessionId = searchParams.get('session_id');
  const paypalMethod = searchParams.get('method');
  const paypalToken = searchParams.get('token');

  const loadOrder = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/orders/${orderId}`);
      setOrder(response.data);
    } catch (err) {
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const capturePayPal = useCallback(async () => {
    if (!paypalToken) { setPollingDone(true); return; }
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payment/paypal/capture/${paypalToken}`);
      setPaymentStatus(response.data.payment_status);
      if (response.data.payment_status === 'paid') {
        const orderRes = await axios.get(`${BACKEND_URL}/api/orders/${orderId}`);
        setOrder(orderRes.data);
      }
    } catch (err) {
      setPaymentStatus('error');
    } finally {
      setPollingDone(true);
    }
  }, [paypalToken, orderId]);

  const pollPaymentStatus = useCallback(async () => {
    if (!sessionId || pollCountRef.current >= 5) { setPollingDone(true); return; }
    pollCountRef.current += 1;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payment/stripe/status/${sessionId}`);
      setPaymentStatus(response.data.payment_status);
      if (response.data.payment_status === 'paid') {
        setPollingDone(true);
        const orderRes = await axios.get(`${BACKEND_URL}/api/orders/${orderId}`);
        setOrder(orderRes.data);
        return;
      }
      if (response.data.status === 'expired') { setPollingDone(true); return; }
      setTimeout(() => pollPaymentStatus(), 2000);
    } catch (err) {
      setPollingDone(true);
    }
  }, [sessionId, orderId]);

  useEffect(() => {
    loadOrder();
    if (paypalMethod === 'paypal' && paypalToken) {
      capturePayPal();
    } else if (sessionId) {
      pollPaymentStatus();
    } else {
      setPollingDone(true);
    }
  }, [loadOrder, capturePayPal, pollPaymentStatus, paypalMethod, paypalToken, sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <p className="text-white text-xl">Chargement...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 text-center py-20">
          <h2 className="text-3xl font-bold text-white mb-4">Commande introuvable</h2>
          <Link to="/catalogue"><Button className="bg-purple-600 hover:bg-purple-700">Retour au catalogue</Button></Link>
        </div>
      </div>
    );
  }

  const statusColors = { pending: 'bg-yellow-500', confirmed: 'bg-blue-500', shipped: 'bg-purple-500', delivered: 'bg-green-500', cancelled: 'bg-red-500' };
  const statusLabels = { pending: 'En attente', confirmed: 'Confirmée', shipped: 'Expédiée', delivered: 'Livrée', cancelled: 'Annulée' };

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12" data-testid="order-confirmation">
          {sessionId && !pollingDone ? (
            <>
              <Loader2 className="w-20 h-20 text-purple-500 mx-auto mb-4 animate-spin" />
              <h1 className="text-4xl font-bold text-white mb-2">Vérification du paiement...</h1>
              <p className="text-gray-400">Veuillez patienter</p>
            </>
          ) : paymentStatus === 'paid' || order?.paymentStatus === 'paid' ? (
            <>
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
              <h1 className="text-4xl font-bold text-white mb-2">Paiement confirmé !</h1>
              <p className="text-gray-400">Merci pour votre achat</p>
            </>
          ) : (
            <>
              <CheckCircle className="w-20 h-20 text-yellow-500 mx-auto mb-4" />
              <h1 className="text-4xl font-bold text-white mb-2">Commande enregistrée</h1>
              <p className="text-gray-400">{paymentStatus === 'expired' ? 'La session de paiement a expiré' : 'Votre commande a été enregistrée'}</p>
            </>
          )}
        </div>

        <div className="bg-gray-900 rounded-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Commande #{order.id.substring(0, 8).toUpperCase()}</h2>
              <p className="text-gray-400">{new Date(order.createdAt).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            <div className={`px-4 py-2 rounded-full ${statusColors[order.status]} text-white font-semibold`}>{statusLabels[order.status]}</div>
          </div>
          <div className="border-t border-gray-800 pt-6">
            <h3 className="text-white font-semibold mb-2">Informations de livraison</h3>
            <p className="text-gray-400">{order.customerName}</p>
            <p className="text-gray-400">{order.customerEmail}</p>
            <p className="text-gray-400">{order.customerPhone}</p>
            <p className="text-gray-400 mt-2 whitespace-pre-line">{order.customerAddress}</p>
          </div>
        </div>

        <div className="bg-gray-900 rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center"><Package className="w-6 h-6 mr-2" />Articles commandés</h2>
          <div className="space-y-4">
            {order.items.map((item, index) => (
              <div key={`${item.productId}-${index}`} className="flex items-center gap-4 pb-4 border-b border-gray-800 last:border-0">
                <img loading="lazy" src={item.image} alt={item.productName} className="w-20 h-20 object-cover rounded" />
                <div className="flex-1">
                  <h3 className="text-white font-semibold">{item.productName}</h3>
                  <p className="text-gray-400 text-sm">Quantité: {item.quantity}</p>
                  <p className="text-gray-400 text-sm">{item.price.toLocaleString('fr-FR')} € / unité</p>
                </div>
                <p className="text-white font-bold text-lg">{(item.price * item.quantity).toLocaleString('fr-FR')} €</p>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-700 pt-4 mt-4">
            <div className="flex justify-between text-2xl font-bold text-white">
              <span>Total</span>
              <span>{order.totalAmount.toLocaleString('fr-FR')} €</span>
            </div>
          </div>
        </div>

        <div className="text-center space-x-4">
          <Link to="/catalogue"><Button className="bg-purple-600 hover:bg-purple-700">Continuer mes achats</Button></Link>
          <Link to="/"><Button variant="outline" className="border-gray-700 text-white hover:bg-gray-800">Retour à l'accueil</Button></Link>
        </div>

        <div className="mt-8 bg-blue-900/30 border border-blue-700/50 rounded-lg p-6">
          <h3 className="text-blue-300 font-semibold mb-2">Que se passe-t-il maintenant ?</h3>
          <ul className="text-blue-300 text-sm space-y-2">
            <li>Vous recevrez un email de confirmation à {order.customerEmail}</li>
            <li>Nous préparons votre commande</li>
            <li>Vous serez notifié dès l'expédition</li>
            <li>Livraison estimée sous 3-5 jours ouvrés</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
